import { createContext, useContext, useEffect, useMemo } from 'react'
import type {
  CatalogJurisdiction,
  CatalogLicenseType,
  FederalSentinel,
  LicenseCatalog,
} from '@/shared/domain'

export const LicenseCatalogCtx = createContext<LicenseCatalog | null>(null)

export const LicenseCatalogRequestLoadCtx = createContext<() => void>(() => {})

const EMPTY_CATALOG: LicenseCatalog = { jurisdictions: [], licenseTypes: [] }

export type LicenseCatalogApi = {
  isResolved: boolean
  jurisdictions: CatalogJurisdiction[]
  licenseTypesByState: Record<string, CatalogLicenseType[]>
  supportedLicenseStates: string[]
  jurisdictionConfig: (state?: string) => CatalogJurisdiction | undefined
  isLicenseStateSupported: (state?: string) => boolean
  getJurisdictionSentinel: (licenseType: string) => FederalSentinel | null
  isFederalLicenseType: (licenseType: string) => boolean
}

export function useLicenseCatalog(): LicenseCatalogApi {
  const requestLoad = useContext(LicenseCatalogRequestLoadCtx)
  useEffect(() => {
    requestLoad()
  }, [requestLoad])
  const catalog = useContext(LicenseCatalogCtx)

  return useMemo<LicenseCatalogApi>(() => {
    const resolved = catalog ?? EMPTY_CATALOG
    const byState: Record<string, CatalogLicenseType[]> = {}
    const byKey: Record<string, CatalogJurisdiction> = {}
    for (const jurisdiction of resolved.jurisdictions) {
      byState[jurisdiction.state] = jurisdiction.licenseTypes
      byKey[jurisdiction.state] = jurisdiction
    }
    const sentinelByType: Record<string, FederalSentinel | null> = {}
    for (const rule of resolved.licenseTypes) {
      sentinelByType[rule.key] = rule.federalSentinel
    }

    return {
      isResolved: catalog !== null,
      jurisdictions: resolved.jurisdictions,
      licenseTypesByState: byState,
      supportedLicenseStates: Object.keys(byState),
      jurisdictionConfig: (state) => (state ? byKey[state] : undefined),
      isLicenseStateSupported: (state) =>
        Boolean(state) && Object.prototype.hasOwnProperty.call(byState, state as string),
      getJurisdictionSentinel: (licenseType) => sentinelByType[licenseType] ?? null,
      isFederalLicenseType: (licenseType) => (sentinelByType[licenseType] ?? null) !== null,
    }
  }, [catalog])
}
